<script>
import axios from 'axios';
import Layout from '../../layouts/main';
import PageHeader from '@/components/page-header';

export default {
  page: { title: 'Notificações de cobranças' },
  components: { Layout, PageHeader },
  data() {
    return {
      title: 'Notificações de cobranças',
      items: [{ text: 'Financeiro' }, { text: 'Notificações', active: true }],
      loading: true,
      saving: false,
      simulating: false,
      changingStatus: false,
      canActivate: false,
      settings: {
        enabled: false,
        timezone: 'America/Sao_Paulo',
        upcoming_enabled: true,
        upcoming_days_before: 3,
        upcoming_time: '09:00',
        due_today_enabled: true,
        due_today_time: '09:00',
        overdue_enabled: true,
        overdue_weekdays: [2],
        overdue_time: '10:00',
        overdue_min_days: 7,
        max_messages_per_minute: 20,
      },
      preview: null,
      runs: [],
      deliveries: [],
      weekdays: [
        { value: 0, text: 'Dom' }, { value: 1, text: 'Seg' }, { value: 2, text: 'Ter' },
        { value: 3, text: 'Qua' }, { value: 4, text: 'Qui' }, { value: 5, text: 'Sex' }, { value: 6, text: 'Sáb' },
      ],
    };
  },
  created() { this.user = JSON.parse(localStorage.getItem('user') || '{}'); this.load(); },
  methods: {
    headers() { return { Accept: 'application/json', Authorization: `Bearer ${this.user.access_token}` }; },
    endpoint(path = '') { return `${process.env.VUE_APP_API}api/v1/admin/billing-notifications${path}`; },
    normalizeSettings(settings) {
      const copy = { ...settings };
      ['upcoming_time', 'due_today_time', 'overdue_time'].forEach(key => { copy[key] = String(copy[key] || '').slice(0, 5); });
      copy.overdue_weekdays = (copy.overdue_weekdays || []).map(Number);
      return copy;
    },
    async load() {
      this.loading = true;
      try {
        const { data } = await axios.get(this.endpoint(), { headers: this.headers() });
        this.settings = this.normalizeSettings(data.data.settings);
        this.canActivate = data.data.can_activate;
        this.runs = data.data.runs || [];
        this.deliveries = data.data.deliveries || [];
      } catch (error) { this.alert(error, 'Não foi possível carregar as configurações.'); }
      finally { this.loading = false; }
    },
    async save() {
      this.saving = true;
      try {
        const payload = { ...this.settings, enabled: undefined, overdue_weekdays: this.settings.overdue_weekdays.map(Number) };
        const { data } = await axios.put(this.endpoint(), payload, { headers: this.headers() });
        this.settings = this.normalizeSettings(data.data);
        this.canActivate = false;
        this.preview = null;
        this.$swal('Configuração salva', data.message, 'success');
      } catch (error) { this.alert(error, 'Não foi possível salvar.'); }
      finally { this.saving = false; }
    },
    async simulate() {
      this.simulating = true;
      try {
        const { data } = await axios.post(this.endpoint('/simulate'), {}, { headers: this.headers() });
        this.preview = data.data.preview;
        this.settings = this.normalizeSettings(data.data.settings);
        this.canActivate = data.data.can_activate;
        this.$nextTick(() => {
          const preview = document.getElementById('notification-preview');
          if (preview) preview.scrollIntoView({ behavior: 'smooth' });
        });
      } catch (error) { this.alert(error, 'Não foi possível simular.'); }
      finally { this.simulating = false; }
    },
    async activate() {
      const confirm = await this.$swal({ title: 'Ativar notificações?', text: 'Os disparos seguirão os horários simulados.', icon: 'warning', showCancelButton: true, confirmButtonText: 'Ativar', cancelButtonText: 'Cancelar' });
      if (!confirm.isConfirmed) return;
      await this.changeStatus('/activate');
    },
    async pause() { await this.changeStatus('/pause'); },
    async changeStatus(path) {
      this.changingStatus = true;
      try {
        const { data } = await axios.post(this.endpoint(path), {}, { headers: this.headers() });
        this.settings = this.normalizeSettings(data.data);
        this.$swal('Tudo certo', data.message, 'success');
        await this.load();
      } catch (error) { this.alert(error, 'Não foi possível alterar o status.'); }
      finally { this.changingStatus = false; }
    },
    alert(error, fallback) {
      const data = error.response && error.response.data;
      const validation = data && data.errors ? [].concat(...Object.values(data.errors)).join('\n') : null;
      this.$swal('Atenção', validation || (data && data.message) || fallback, 'error');
    },
    typeLabel(type) { return { upcoming: 'Aviso antecipado', due_today: 'Vence hoje', overdue: 'Vencidas' }[type] || type; },
    statusLabel(status) { return { pending: 'Pendente', processing: 'Preparando', queued: 'Na fila', sending: 'Enviando', sent: 'Enviada', skipped: 'Ignorada', failed: 'Falhou', completed: 'Concluída', completed_with_errors: 'Concluída com falhas', retrying: 'Nova tentativa' }[status] || status; },
    statusVariant(status) { return ['sent', 'completed'].includes(status) ? 'success' : ['failed', 'completed_with_errors'].includes(status) ? 'danger' : ['skipped'].includes(status) ? 'secondary' : 'warning'; },
    money(value) { return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }); },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <b-alert show :variant="settings.enabled ? 'success' : 'warning'" class="d-flex align-items-center justify-content-between">
      <span><strong>{{ settings.enabled ? 'Notificações ativas' : 'Notificações pausadas' }}</strong><br><small>Fuso horário: Brasília. Pagamentos são conferidos novamente antes de cada envio.</small></span>
      <b-button v-if="settings.enabled" variant="outline-danger" size="sm" :disabled="changingStatus" @click="pause">Pausar</b-button>
      <b-button v-else variant="success" size="sm" :disabled="!canActivate || changingStatus" @click="activate">Ativar após simulação</b-button>
    </b-alert>

    <div v-if="!loading" class="row">
      <div class="col-xl-4">
        <b-card title="Aviso antecipado" class="h-100">
          <b-form-checkbox v-model="settings.upcoming_enabled" switch>Ativar aviso antes do vencimento</b-form-checkbox>
          <b-form-group label="Quantos dias antes" class="mt-3"><b-form-input v-model.number="settings.upcoming_days_before" type="number" min="1" max="30" /></b-form-group>
          <b-form-group label="Horário"><b-form-input v-model="settings.upcoming_time" type="time" /></b-form-group>
        </b-card>
      </div>
      <div class="col-xl-4">
        <b-card title="Vencimento no dia" class="h-100">
          <b-form-checkbox v-model="settings.due_today_enabled" switch>Enviar mensagem no dia do vencimento</b-form-checkbox>
          <b-form-group label="Horário" class="mt-3"><b-form-input v-model="settings.due_today_time" type="time" /></b-form-group>
          <p class="text-muted small">Cada parcela será avisada uma única vez no dia.</p>
        </b-card>
      </div>
      <div class="col-xl-4">
        <b-card title="Cobranças vencidas" class="h-100">
          <b-form-checkbox v-model="settings.overdue_enabled" switch>Notificar cobranças atrasadas</b-form-checkbox>
          <b-form-group label="Dias de envio" class="mt-3"><b-form-checkbox-group v-model="settings.overdue_weekdays" :options="weekdays" buttons button-variant="outline-primary" size="sm" /></b-form-group>
          <div class="row"><b-form-group label="Atraso mínimo" class="col"><b-form-input v-model.number="settings.overdue_min_days" type="number" min="7" /></b-form-group><b-form-group label="Horário" class="col"><b-form-input v-model="settings.overdue_time" type="time" /></b-form-group></div>
        </b-card>
      </div>
    </div>

    <b-card v-if="!loading" class="mt-3">
      <div class="row align-items-end">
        <b-form-group label="Máximo de mensagens por minuto" class="col-md-4 mb-0"><b-form-input v-model.number="settings.max_messages_per_minute" type="number" min="1" max="60" /></b-form-group>
        <div class="col-md-8 text-right">
          <b-button variant="outline-primary" class="mr-2" :disabled="saving" @click="save"><i class="ri-save-line"></i> Salvar e pausar</b-button>
          <b-button variant="primary" :disabled="simulating" @click="simulate"><i class="ri-search-eye-line"></i> {{ simulating ? 'Simulando...' : 'Simular agora' }}</b-button>
        </div>
      </div>
      <small class="text-muted">Salvar qualquer alteração pausa os disparos e exige uma nova simulação.</small>
    </b-card>

    <div v-if="preview" id="notification-preview" class="mt-4">
      <h5>Resultado da simulação — nenhuma mensagem foi enviada</h5>
      <div class="row">
        <div v-for="(result, type) in preview" :key="type" class="col-lg-4">
          <b-card :title="typeLabel(type)">
            <div class="preview-totals"><strong>{{ result.recipients }}</strong> comprador(es) · <strong>{{ result.installments }}</strong> parcela(s)</div>
            <div v-if="result.invalid_phones" class="text-danger small mt-1">{{ result.invalid_phones }} comprador(es) sem telefone válido</div>
            <div v-for="sample in result.samples" :key="sample.client_id" class="message-preview mt-3">
              <strong>{{ sample.client_name }}</strong> · {{ sample.installments }} parcela(s) · {{ money(sample.total) }}
              <pre>{{ sample.message }}</pre>
            </div>
            <div v-if="!result.samples.length" class="text-muted mt-3">Nenhum destinatário elegível hoje.</div>
          </b-card>
        </div>
      </div>
    </div>

    <b-card title="Execuções recentes" class="mt-3">
      <div class="table-responsive"><table class="table table-sm mb-0"><thead><tr><th>Data</th><th>Tipo</th><th>Status</th><th>Parcelas</th><th>Mensagens</th><th>Enviadas</th><th>Ignoradas</th><th>Falhas</th></tr></thead><tbody><tr v-for="run in runs" :key="run.id"><td>{{ $moment(run.created_at).format('DD/MM/YYYY HH:mm') }}</td><td>{{ typeLabel(run.type) }}</td><td><b-badge :variant="statusVariant(run.status)">{{ statusLabel(run.status) }}</b-badge></td><td>{{ run.candidate_count }}</td><td>{{ run.delivery_count }}</td><td>{{ run.sent_count }}</td><td>{{ run.skipped_count }}</td><td>{{ run.failed_count }}</td></tr><tr v-if="!runs.length"><td colspan="8" class="text-center text-muted">Nenhuma execução registrada.</td></tr></tbody></table></div>
    </b-card>

    <b-card title="Mensagens recentes" class="mt-3">
      <div class="table-responsive"><table class="table table-sm mb-0"><thead><tr><th>Data</th><th>Comprador</th><th>Tipo</th><th>Parcelas</th><th>Total original</th><th>Status</th><th>Motivo</th></tr></thead><tbody><tr v-for="delivery in deliveries" :key="delivery.id"><td>{{ $moment(delivery.created_at).format('DD/MM/YYYY HH:mm') }}</td><td>{{ delivery.client ? delivery.client.name : delivery.phone }}</td><td>{{ typeLabel(delivery.notification_type) }}</td><td>{{ delivery.items_count }}</td><td>{{ money(delivery.total_amount) }}</td><td><b-badge :variant="statusVariant(delivery.status)">{{ statusLabel(delivery.status) }}</b-badge></td><td class="small text-danger">{{ delivery.error }}</td></tr><tr v-if="!deliveries.length"><td colspan="7" class="text-center text-muted">Nenhuma mensagem registrada.</td></tr></tbody></table></div>
    </b-card>
  </Layout>
</template>

<style scoped>
.message-preview{border:1px solid #e9ecef;border-radius:8px;padding:10px;background:#f8f9fa}.message-preview pre{font:12px/1.45 inherit;white-space:pre-wrap;margin:8px 0 0;color:#495057}.preview-totals{font-size:14px}
</style>
